import {
  ApolloClient, ApolloLink, InMemoryCache, Operation,
} from '@apollo/client';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { onError } from '@apollo/client/link/error';
import { createUploadLink } from 'apollo-upload-client';
import { WebSocketLink } from '@apollo/client/link/ws';

// Utils
import { routes } from 'utils/routes';
import { Logger } from './utils/logger';

// Logger instance
const logger = new Logger('client.ts');

// Links

const terminatingLink = new BatchHttpLink({
  credentials: process.env.NODE_ENV === 'development' ? 'same-origin' : 'include',
  uri: `${import.meta.env.VITE_API || ''}/`,
});

// Upload Link
const terminatingUploadLink = createUploadLink({
  credentials: process.env.NODE_ENV === 'development' ? 'same-origin' : 'include',
  uri: `${import.meta.env.VITE_API || ''}/`,
});

// WS Link
const wsURL = `${import.meta.env.VITE_API_WS || ''}`;
const terminatingWSLink = new WebSocketLink({
  uri: wsURL,
  options: {
    connectionParams: {
      credentials: process.env.NODE_ENV === 'development' ? 'same-origin' : 'include',
    },
    reconnect: true,
    reconnectionAttempts: 10,
    lazy: true, // NOTE: only connect when first subscribing
    connectionCallback: (errors: Error[], result?: any) => {
      if (errors && errors.length > 0) {
        // eslint-disable-next-line no-console
        console.log('WS [connectionCallback] ERRORS', errors);
      }
      if (result) {
        // eslint-disable-next-line no-console
        console.log('WS [connectionCallback] Result', result);
      }
    },
  },
});

// NOTE: Decides whether the operation is a file upload operation
const isUploadLink = function isUploadLink(operation: Operation) {
  const ctx = operation.getContext();
  const out = !!(ctx.fetchOptions?.onUploadProgress || ctx.isUpload);
  return out;
};

// NOTE: Decides whether the operation is for subscriptions
const isWS = function isWS(operation: Operation) {
  const ctx = operation.getContext();
  const out = !!(ctx.isSubscription);
  return out;
};

// NOTE: We use directional links to direct the request to a target API
// NOTE: https://www.apollographql.com/docs/react/api/link/introduction/#directional-composition

// Global Directional Links
const directionalWSLink = ApolloLink.split(isWS, terminatingWSLink, terminatingLink);
const directionalLink = ApolloLink.split(isUploadLink, terminatingUploadLink, directionalWSLink);

// log errors
const superLink = ApolloLink.from([
  onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path }) => {
        logger.error(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
        );
        // Handle when auth error and you are inside the app, redirect to login
        if (message === 'AUTH_ERROR') {
          const publicPaths = routes.public.map(r => r.path);
          if (!publicPaths.includes(window.location.pathname)) {
            window.location.replace(`${window.location.origin}/login`);
          }
        }
      });
    }

    if (networkError) {
      logger.error(`[Network error]: ${networkError}`);
    }
  }),
  directionalLink,
]);

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        getPaginatedClients: {
          keyArgs: ['orderBy', 'where'],
          // Concatenate the incoming list items with
          // the existing list items.
          merge(existing, incoming, { args }) {
            const existingClients = existing?.clients || [];
            if ((args?.skip ?? 0) === 0) {
              return incoming;
            }

            return ({
              ...incoming,
              clients: [...existingClients, ...incoming.clients],
            });
          },
        },
        getPaginatedTasks: {
          keyArgs: ['orderBy', 'where'],
          // Concatenate the incoming list items with
          // the existing list items.
          merge(existing, incoming, { args }) {
            const existingTasks = existing?.tasks || [];
            if ((args?.skip ?? 0) === 0) {
              return incoming;
            }
            return ({
              ...incoming,
              tasks: [...existingTasks, ...incoming.tasks],
            });
          },
        },
        getPaginatedGeneratedInvoices: {
          keyArgs: ['orderBy', 'where'],
          // Concatenate the incoming list items with
          // the existing list items.
          merge(existing, incoming, { args }) {
            const existingItems = existing?.generatedInvoices || [];
            if ((args?.skip ?? 0) === 0) {
              return incoming;
            }
            return ({
              ...incoming,
              generatedInvoices: [...existingItems, ...incoming.generatedInvoices],
            });
          },
        },
        getPaginatedNotifications: {
          keyArgs: ['orderBy', 'where'],
          // Concatenate the incoming list items with
          // the existing list items.
          merge(existing, incoming, { args }) {
            const existingItems = existing?.notifications || [];
            if ((args?.skip ?? 0) === 0) {
              return incoming;
            }
            return ({
              ...incoming,
              notifications: [...existingItems, ...incoming.notifications],
            });
          },
        },
        getPaginatedProposalAndContracts: {
          keyArgs: ['orderBy', 'where'],
          // Concatenate the incoming list items with
          // the existing list items.
          merge(existing, incoming, { args }) {
            const existingItems = existing?.proposalsAndContracts || [];
            if ((args?.skip ?? 0) === 0) {
              return incoming;
            }
            return ({
              ...incoming,
              proposalsAndContracts: [...existingItems, ...incoming.proposalsAndContracts],
            });
          },
        },
        search: {
          keyArgs: ['type', 'value'],
          // Concatenate the incoming list items with
          // the existing list items.
          merge(existing, incoming, { args }) {
            const existingSearchResults = existing?.items || [];
            if ((args?.skip ?? 0) === 0) {
              return incoming;
            }

            return ({
              ...incoming,
              items: [...existingSearchResults, ...incoming.items],
            });
          },
        },
        getPaginatedNotes: {
          keyArgs: ['where'],
          // Concatenate the incoming list items with
          // the existing list items.
          merge(existing, incoming, { args }) {
            const existingNotes = existing?.notes || [];
            if ((args?.skip ?? 0) === 0) {
              return incoming;
            }

            return ({
              ...incoming,
              notes: [...existingNotes, ...incoming.notes],
            });
          },
        },
        getPaginatedTimes: {
          keyArgs: ['orderBy', 'where'],
          // Concatenate the incoming list items with
          // the existing list items.
          merge(existing, incoming, { args }) {
            const existingTimes = existing?.times || [];
            if ((args?.skip ?? 0) === 0) {
              return incoming;
            }
            return ({
              ...incoming,
              times: [...existingTimes, ...incoming.times],
            });
          },
        },
        getPaginatedOrganizationUsers: {
          keyArgs: ['orderBy', 'where'],
          // Concatenate the incoming list items with
          // the existing list items.
          merge(existing, incoming, { args }) {
            const existingOrganizationUsers = existing?.organizationUsers || [];
            if ((args?.skip ?? 0) === 0) {
              return incoming;
            }
            return ({
              ...incoming,
              organizationUsers: [...existingOrganizationUsers, ...incoming.organizationUsers],
            });
          },
        },
        stripeGetInvoices: {
          keyArgs: [],
          // Concatenate the incoming list items with
          // the existing list items.
          merge(existing, incoming, { args }) {
            const existingInvoices = existing?.invoices || [];
            if (!args?.lastId) {
              return incoming;
            }
            return ({
              ...incoming,
              invoices: [...existingInvoices, ...incoming.invoices],
            });
          },
        },
      },
    },
  },
});

export const client = new ApolloClient({
  cache,
  link: superLink,
});
